import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import '../styles/CustomOrderManagement.css';

const CustomOrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchCustomOrders();
  }, []);

  const fetchCustomOrders = async () => {
    try {
      // Try with orderBy first, fall back to simple query if index not available
      let q;
      try {
        q = query(collection(db, 'customOrders'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const ordersData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));
        setOrders(ordersData);
      } catch (indexError) {
        // Fallback to simple query without orderBy
        const querySnapshot = await getDocs(collection(db, 'customOrders'));
        const ordersData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));
        // Sort manually by createdAt
        ordersData.sort((a, b) => (b.createdAt || new Date()) - (a.createdAt || new Date()));
        setOrders(ordersData);
      }
    } catch (error) {
      console.error('Error fetching custom orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, 'customOrders', orderId), {
        status: newStatus
      });
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    }
  };

  const deleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await deleteDoc(doc(db, 'customOrders', orderId));
        setOrders(orders.filter(order => order.id !== orderId));
      } catch (error) {
        console.error('Error deleting order:', error);
        alert('Failed to delete order');
      }
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'confirmed': return '#27ae60';
      case 'in-progress': return '#3498db';
      case 'completed': return '#2ecc71';
      case 'cancelled': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  if (loading) {
    return <div className="loading">Loading custom orders...</div>;
  }

  return (
    <div className="custom-order-management">
      <div className="management-header">
        <h1>Custom Order Management</h1>
        <div className="filter-controls">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Orders</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="orders-grid">
        {filteredOrders.length === 0 ? (
          <div className="no-orders">
            <p>No custom orders found.</p>
          </div>
        ) : (
          filteredOrders.map(order => (
            <div key={order.id} className="order-card">
              <div className="order-header">
                <h3>{order.customerName}</h3>
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(order.status) }}
                >
                  {order.status}
                </span>
              </div>

              <div className="order-details">
                <div className="detail-row">
                  <strong>Event:</strong> {order.eventType}
                </div>
                <div className="detail-row">
                  <strong>Date:</strong> {order.eventDate} at {order.eventTime}
                </div>
                <div className="detail-row">
                  <strong>Guests:</strong> {order.guestCount} people
                </div>
                <div className="detail-row">
                  <strong>Venue:</strong> {order.venue}
                </div>
                <div className="detail-row">
                  <strong>Contact:</strong> {order.email} | {order.phone}
                </div>
                {order.budget && (
                  <div className="detail-row">
                    <strong>Budget:</strong> {order.budget}
                  </div>
                )}
                <div className="detail-row">
                  <strong>Description:</strong>
                  <p className="description">{order.eventDescription}</p>
                </div>
                {order.specialRequirements && (
                  <div className="detail-row">
                    <strong>Special Requirements:</strong>
                    <p className="description">{order.specialRequirements}</p>
                  </div>
                )}
                <div className="detail-row">
                  <strong>Submitted:</strong> {order.createdAt?.toLocaleDateString()} {order.createdAt?.toLocaleTimeString()}
                </div>
              </div>

              <div className="order-actions">
                <select
                  value={order.status}
                  onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                  className="status-select"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  onClick={() => deleteOrder(order.id)}
                  className="delete-btn"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CustomOrderManagement;